import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import SEO from "../components/SEO";

const TermsPage = () => {
  return (
    <div className="pt-32" data-testid="terms-page">
      <SEO 
        title="Terms of Service"
        description="Read OptimaGrowth's terms of service. Understand the terms and conditions that govern your use of our website and marketing services."
        keywords="terms of service, terms and conditions, service agreement, OptimaGrowth terms"
      />
      <section className="section">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="font-heading text-4xl font-bold text-optima-dark mb-6">
                Terms of Service
              </h1>
              <p className="text-slate-600 mb-8">
                Last updated: January 2026
              </p>

              <div className="prose max-w-none">
                <h2 className="font-heading text-2xl font-bold text-optima-dark mt-8 mb-4">
                  1. Agreement to Terms
                </h2>
                <p className="text-slate-700 mb-4">
                  By accessing or using the OptimaGrowth website (optimagrowth.agency) and services, you agree to be bound by these Terms of Service. If you disagree with any part of these terms, you may not access our services.
                </p>

                <h2 className="font-heading text-2xl font-bold text-optima-dark mt-8 mb-4">
                  2. Services Description
                </h2>
                <p className="text-slate-700 mb-4">
                  OptimaGrowth provides marketing automation, growth strategy, and digital marketing services. Our services include but are not limited to:
                </p>
                <ul className="list-disc pl-6 mb-4 text-slate-700">
                  <li>Business growth strategy and consulting</li>
                  <li>Marketing automation implementation</li>
                  <li>CRM setup and management</li>
                  <li>Lead generation systems</li>
                  <li>SEO and content marketing</li>
                  <li>Paid advertising management</li>
                  <li>Software as a Service (OptimaGrowth Software)</li>
                </ul>

                <h2 className="font-heading text-2xl font-bold text-optima-dark mt-8 mb-4">
                  3. User Responsibilities
                </h2>
                <p className="text-slate-700 mb-4">
                  When using our services, you agree to:
                </p>
                <ul className="list-disc pl-6 mb-4 text-slate-700">
                  <li>Provide accurate and complete information</li>
                  <li>Maintain the confidentiality of your account credentials</li>
                  <li>Use our services only for lawful purposes</li>
                  <li>Not interfere with or disrupt our services</li>
                  <li>Not attempt to gain unauthorized access to our systems</li>
                  <li>Comply with all applicable laws and regulations</li>
                </ul>

                <h2 className="font-heading text-2xl font-bold text-optima-dark mt-8 mb-4">
                  4. Service Agreements and Retainers
                </h2>
                <p className="text-slate-700 mb-4">
                  Specific services may be subject to separate service agreements or statements of work. These agreements will specify:
                </p>
                <ul className="list-disc pl-6 mb-4 text-slate-700">
                  <li>Scope of work and deliverables</li>
                  <li>Pricing and payment terms</li>
                  <li>Timeline and milestones</li>
                  <li>Termination conditions</li>
                </ul>
                <p className="text-slate-700 mb-4">
                  Monthly retainer agreements typically require 30 days written notice for cancellation unless otherwise specified.
                </p>

                <h2 className="font-heading text-2xl font-bold text-optima-dark mt-8 mb-4">
                  5. Payment Terms
                </h2>
                <p className="text-slate-700 mb-4">
                  Payment terms are specified in individual service agreements. General terms include:
                </p>
                <ul className="list-disc pl-6 mb-4 text-slate-700">
                  <li>Retainer fees are due at the beginning of each service period</li>
                  <li>Project fees may be invoiced as specified in the agreement</li>
                  <li>Late payments may incur interest at 2% per month</li>
                  <li>All prices are quoted in the specified currency (ZAR, GBP, or USD)</li>
                </ul>

                <h2 className="font-heading text-2xl font-bold text-optima-dark mt-8 mb-4">
                  6. Intellectual Property
                </h2>
                <h3 className="font-heading text-xl font-semibold text-optima-dark mt-6 mb-3">
                  6.1 Our Content
                </h3>
                <p className="text-slate-700 mb-4">
                  All content on our website, including text, graphics, logos, and software, is the property of OptimaGrowth or our licensors and is protected by intellectual property laws.
                </p>
                <h3 className="font-heading text-xl font-semibold text-optima-dark mt-6 mb-3">
                  6.2 Client Work
                </h3>
                <p className="text-slate-700 mb-4">
                  Unless otherwise specified in a service agreement, upon full payment, clients receive ownership of custom deliverables created specifically for them. We retain ownership of our proprietary tools, processes, and methodologies.
                </p>

                <h2 className="font-heading text-2xl font-bold text-optima-dark mt-8 mb-4">
                  7. Confidentiality
                </h2>
                <p className="text-slate-700 mb-4">
                  We agree to keep confidential any proprietary information shared by clients in the course of providing services. Clients agree to keep confidential our proprietary methodologies and business information.
                </p>

                <h2 className="font-heading text-2xl font-bold text-optima-dark mt-8 mb-4">
                  8. Limitation of Liability
                </h2>
                <p className="text-slate-700 mb-4">
                  To the maximum extent permitted by law:
                </p>
                <ul className="list-disc pl-6 mb-4 text-slate-700">
                  <li>Our services are provided "as is" without warranties of any kind</li>
                  <li>We are not liable for indirect, incidental, or consequential damages</li>
                  <li>Our total liability is limited to the fees paid for the specific service at issue</li>
                  <li>We do not guarantee specific business results from our services</li>
                </ul>

                <h2 className="font-heading text-2xl font-bold text-optima-dark mt-8 mb-4">
                  9. Indemnification
                </h2>
                <p className="text-slate-700 mb-4">
                  You agree to indemnify and hold OptimaGrowth harmless from any claims, damages, or expenses arising from your use of our services or violation of these terms.
                </p>

                <h2 className="font-heading text-2xl font-bold text-optima-dark mt-8 mb-4">
                  10. Third-Party Services
                </h2>
                <p className="text-slate-700 mb-4">
                  Our services may integrate with or recommend third-party services (e.g., GoHighLevel, Google Ads, Meta). We are not responsible for the terms, privacy practices, or performance of these third-party services.
                </p>

                <h2 className="font-heading text-2xl font-bold text-optima-dark mt-8 mb-4">
                  11. Termination
                </h2>
                <p className="text-slate-700 mb-4">
                  We may terminate or suspend access to our services immediately, without prior notice, for any breach of these terms. Upon termination, your right to use our services ceases immediately.
                </p>

                <h2 className="font-heading text-2xl font-bold text-optima-dark mt-8 mb-4">
                  12. Governing Law
                </h2>
                <p className="text-slate-700 mb-4">
                  These terms are governed by the laws of South Africa. Any disputes will be resolved in the courts of Cape Town, South Africa, unless otherwise agreed in writing.
                </p>

                <h2 className="font-heading text-2xl font-bold text-optima-dark mt-8 mb-4">
                  13. Changes to Terms
                </h2>
                <p className="text-slate-700 mb-4">
                  We reserve the right to modify these terms at any time. Changes will be effective when posted on this page. Continued use of our services after changes constitutes acceptance of the modified terms.
                </p>

                <h2 className="font-heading text-2xl font-bold text-optima-dark mt-8 mb-4">
                  14. Contact Information
                </h2>
                <p className="text-slate-700 mb-4">
                  For questions about these Terms of Service, please contact us:
                </p>
                <p className="text-slate-700 mb-4">
                  <strong>OptimaGrowth</strong><br />
                  Email: info@optimagrowth.agency<br />
                  Phone: +27 66 122 9790<br />
                  Location: Cape Town, South Africa
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsPage;
